import {
    Box,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon,
    Heading,
    Text,
    Code,
    OrderedList,
    ListItem,
    Link,
    VStack,
} from '@chakra-ui/react';
import { StorageProvider } from './StorageProviderSelector';

interface StorageProviderInstructionsProps {
    provider: StorageProvider;
}

export const StorageProviderInstructions = ({
    provider,
}: StorageProviderInstructionsProps) => {
    if (provider === 'local') return null;

    const instructions = {
        azure: {
            title: 'How to generate an Azure ADLS SAS Token',
            steps: [
                'Navigate to Azure Portal → Storage Account → Containers',
                'Click into your container and browse to the specific file',
                'Click on the file to open blob details',
                'Click "Generate SAS" tab at the top',
                'Configure: Permissions = Read, Expiry = 6-8 hours',
                'Click "Generate SAS token and URL"',
                'Copy the "Blob SAS URL" (full URL, not just the token)',
            ],
            docs: 'https://learn.microsoft.com/en-us/azure/storage/common/storage-sas-overview',
            example: 'https://mystorageaccount.blob.core.windows.net/mycontainer/path/file.cram?sv=2022-11-02&ss=b&srt=o&sp=r&se=...',
        },
        aws: {
            title: 'How to generate an AWS S3 Presigned URL',
            steps: [
                'Open the AWS Management Console and sign in',
                'Navigate to S3 service',
                'Find your bucket and locate the BAM/CRAM file',
                'Select the file and click "Actions"',
                'Choose "Share with a presigned URL"',
                'Set expiration time (recommended: 1-2 hours)',
                'Click "Create presigned URL"',
                'Copy the generated URL and paste it above',
            ],
            docs: 'https://docs.aws.amazon.com/AmazonS3/latest/userguide/ShareObjectPreSignedURL.html',
            example: 'https://mybucket.s3.us-east-1.amazonaws.com/sample.cram?X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=...',
        },
        gcp: {
            title: 'How to generate a Google Cloud Storage Signed URL',
            steps: [
                'Open Google Cloud Console and sign in',
                'Navigate to Cloud Storage',
                'Find your bucket and the BAM/CRAM file',
                'Click the three dots next to the file',
                'Select "Create signed URL"',
                'Set expiration time (recommended: 1-2 hours)',
                'Click "Create"',
                'Copy the signed URL and paste it above',
            ],
            cliExample: 'gcloud storage sign-url gs://mybucket/sample.cram --duration=1h',
            docs: 'https://cloud.google.com/storage/docs/access-control/signed-urls',
            example: 'https://storage.googleapis.com/mybucket/sample.cram?X-Goog-Algorithm=GOOG4-RSA-SHA256&X-Goog-Credential=...',
        },
    };

    const content = instructions[provider];
    if (!content) return null;

    return (
        <Box maxW="800px" mx="auto" mt={8} mb={20}>
            <Accordion allowToggle>
                <AccordionItem border="1px" borderColor="surface.300" borderRadius="lg">
                    <h2>
                        <AccordionButton
                            _expanded={{ bg: 'surface.200', color: 'primary.500' }}
                            borderRadius="lg"
                            py={4}
                        >
                            <Box flex="1" textAlign="left">
                                <Heading size="md">📖 {content.title}</Heading>
                            </Box>
                            <AccordionIcon />
                        </AccordionButton>
                    </h2>
                    <AccordionPanel pb={6} pt={4} textAlign="left">
                        <VStack align="stretch" spacing={4}>
                            <OrderedList spacing={2} pl={4}>
                                {content.steps.map((step, index) => (
                                    <ListItem key={index} fontSize="md" color="primary.500">
                                        {step}
                                    </ListItem>
                                ))}
                            </OrderedList>

                            {'cliExample' in content && (
                                <Box>
                                    <Text fontWeight="semibold" mb={2}>
                                        Using CLI:
                                    </Text>
                                    <Code
                                        display="block"
                                        p={3}
                                        borderRadius="md"
                                        bg="surface.200"
                                        whiteSpace="pre-wrap"
                                        wordBreak="break-all"
                                    >
                                        {content.cliExample}
                                    </Code>
                                </Box>
                            )}

                            <Box>
                                <Text fontWeight="semibold" mb={2}>
                                    Example URL format:
                                </Text>
                                <Code
                                    display="block"
                                    p={3}
                                    borderRadius="md"
                                    bg="surface.200"
                                    whiteSpace="pre-wrap"
                                    wordBreak="break-all"
                                    fontSize="sm"
                                >
                                    {content.example}
                                </Code>
                            </Box>

                            <Box borderTop="1px" borderColor="surface.300" pt={4}>
                                <Link href={content.docs} isExternal color="primary.500" fontWeight="medium">
                                    📚 Official Documentation →
                                </Link>
                            </Box>
                        </VStack>
                    </AccordionPanel>
                </AccordionItem>
            </Accordion>
        </Box>
    );
};
